import { motion } from 'framer-motion';
import { ArrowRight, Globe, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { cn } from '@/Domain/utils/cn';

interface QuickchatDomainNoticeProps {
  submittedUrl: string;
  onCorrect: () => void;
  onProceed: () => void;
}

const containerVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } },
};

const bubbleVariants = {
  hidden: { opacity: 0, y: 12, scale: 0.97 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.3, ease: 'easeOut', delay: 0.12 + i * 0.08 },
  }),
};

function displayHost(url: string): string {
  try {
    return new URL(url.startsWith('http') ? url : `https://${url}`).hostname;
  } catch {
    return url;
  }
}

const MockBubble = ({
  index,
  variant,
  hostLabel,
  message,
  hostIcon,
}: {
  index: number;
  variant: 'us' | 'yours';
  hostLabel: string;
  message: string;
  hostIcon: 'sparkles' | 'globe';
}) => {
  const Icon = hostIcon === 'sparkles' ? Sparkles : Globe;
  return (
    <motion.div variants={bubbleVariants} custom={index} className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground px-1">
        <Icon
          className={cn(
            'size-3 shrink-0',
            variant === 'us' ? 'text-muted-foreground' : 'text-success',
          )}
        />
        <span className="break-words min-w-0">{hostLabel}</span>
      </div>
      <div
        className={cn(
          'rounded-2xl rounded-tl-md px-3.5 py-2.5 text-sm leading-relaxed',
          variant === 'us'
            ? 'bg-muted text-foreground'
            : 'bg-success-muted/60 text-foreground border border-success/20',
        )}
      >
        {message}
      </div>
    </motion.div>
  );
};

export const QuickchatDomainNotice = ({
  submittedUrl,
  onCorrect,
  onProceed,
}: QuickchatDomainNoticeProps) => {
  const { t } = useTranslation('getting_started');
  const host = displayHost(submittedUrl);

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="flex flex-col gap-5 bg-card border border-border rounded-2xl p-6"
      data-testid="gs-quickchat-domain-notice"
      role="alertdialog"
      aria-labelledby="gs-quickchat-domain-notice-title"
    >
      <div className="flex flex-col gap-1.5">
        <h2 id="gs-quickchat-domain-notice-title" className="text-xl font-semibold text-foreground">
          {t('quickchat_domain_notice.title')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('quickchat_domain_notice.body', { host })}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <MockBubble
          index={0}
          variant="us"
          hostIcon="sparkles"
          hostLabel={t('quickchat_domain_notice.mock_us_label', { host })}
          message={t('quickchat_domain_notice.mock_us_message')}
        />
        <MockBubble
          index={1}
          variant="yours"
          hostIcon="globe"
          hostLabel={t('quickchat_domain_notice.mock_yours_label')}
          message={t('quickchat_domain_notice.mock_yours_message')}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Button
          type="button"
          size="md"
          onClick={onCorrect}
          data-testid="gs-quickchat-domain-notice-correct"
          className="w-full"
        >
          {t('quickchat_domain_notice.correct_cta')}
        </Button>
        <Button
          type="button"
          variant="link"
          size="sm"
          onClick={onProceed}
          data-testid="gs-quickchat-domain-notice-proceed"
          className="!text-foreground/60 hover:!text-foreground self-center"
        >
          {t('quickchat_domain_notice.proceed_cta', { host })}
          <ArrowRight />
        </Button>
      </div>
    </motion.div>
  );
};
